<template>
  <div>
    <div
      class="flex h-13"
      @mouseover="hoverState = true"
      @mouseleave="hoverState = false"
    >
      <div class="sticky left-0 z-10 flex pl-6 bg-pure-white">
        <div
          class="flex items-center flex-shrink-0 w-20 px-4 transition-all duration-300 border-b border-l border-slate-400"
          :class="[
            isLast ? 'rounded-bl' : '',
            hoverState ? 'bg-hover-state' : 'bg-pure-white'
          ]"
        >
          <BlTextBody2 :text="credit.id" class="truncate text-slate-1000" />
        </div>
        <div
          class="flex items-center flex-shrink-0 w-40 gap-2 px-4 transition-all duration-300 border-b border-r border-slate-400"
          :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
        >
          <BlTextBody2
            :title="dateCreated"
            :text="dateCreated"
            class="truncate text-slate-1000"
          />
        </div>
      </div>
      <div
        class="flex items-center flex-shrink-0 px-4 transition-all duration-300 border-b w-50 border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2
          :title="credit.accountable_string"
          :text="credit.accountable_string"
          class="truncate"
        />
      </div>
      <div
        class="flex items-center flex-shrink-0 px-4 transition-all duration-300 border-b w-35 border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlChip :chip="credit.type" dense />
      </div>
      <div
        class="flex items-center flex-shrink-0 px-4 transition-all duration-300 border-b w-50 border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2
          :title="serviceItem"
          :text="serviceItem"
          class="truncate"
        />
      </div>
      <div
        class="flex items-center flex-shrink-0 px-4 transition-all duration-300 border-b w-35 border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2 :text="sessionCount" />
      </div>
      <div
        class="flex items-center flex-shrink-0 px-4 transition-all duration-300 border-b w-35 border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2 :text="rescheduleCount" />
      </div>
      <div
        class="flex items-center justify-between flex-shrink-0 px-4 transition-all duration-300 border-b w-50 border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2 :text="currentCenter.currency_sym" />
        <BlTextBody2 :text="amount" />
      </div>
      <div
        class="flex items-center flex-shrink-0 px-4 transition-all duration-300 border-b w-35 border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2
          :title="expirationDate"
          :text="expirationDate"
          class="truncate"
        />
      </div>
      <div
        class="flex items-center flex-grow flex-shrink-0 px-4 transition-all duration-300 border-b w-100 border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2
          :title="credit.reason"
          :text="credit.reason"
          class="truncate whitespace-pre-line"
        />
      </div>
      <div class="sticky right-0 z-10 flex pr-6 bg-pure-white">
        <div
          class="flex items-center justify-center flex-shrink-0 transition-all duration-300 border-b border-r w-30 border-slate-400"
          :class="[
            isLast ? 'rounded-br' : '',
            hoverState ? 'bg-hover-state' : 'bg-pure-white'
          ]"
        >
          <router-link
            v-if="credit.appointment_id && currentUser.role.includes('admin')"
            :to="`/appointments/details/${credit.appointment_id}`"
            target="_blank"
          >
            <BlInputButton
              title="View appointment details"
              label="View details"
              type="outlined"
              dense
            />
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { displayDate, displayPrice } from "@/plugins/helper.js";

import BlTextBody2 from "../../BlTextBody2.vue";
import BlChip from "../../BlChip.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "ClientCreditTransactionItem",
  components: {
    BlTextBody2,
    BlChip,
    BlInputButton
  },
  props: {
    credit: { type: Object, required: true },
    isLast: { type: Boolean, default: false }
  },
  data() {
    return {
      hoverState: false
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter"]),
    dateCreated() {
      const date = this.credit.created_at;
      return this.displayDate({ date, format: "DD MMM YYYY hh:mm" });
    },
    serviceItem() {
      const services = this.credit.credit_transaction_services || [];
      if (!services.length) return "";
      if (services.length === 1) return services[0].package_item_name;
      return `Mixed items (${services.length})`;
    },
    sessionCount() {
      if (this.credit.type === "CreditTransaction::Addition") {
        if (this.credit.session_credit === 0) return "0";
        return `+${this.credit.session_credit}`;
      }
      if (this.credit.type === "CreditTransaction::Subtraction") {
        if (this.credit.session_credit === 0) return "";
        if (this.credit.session_credit === null) return "";
        return `(${this.credit.session_credit})`;
      }
    },
    rescheduleCount() {
      if (this.credit.type === "CreditTransaction::Addition") {
        if (this.credit.reschedule_credit === 0) return "0";
        if (this.credit.reschedule_credit === null) return "Unlimited";
        return `+${this.credit.reschedule_credit}`;
      }
      if (this.credit.type === "CreditTransaction::Subtraction") {
        if (this.credit.reschedule_credit === 0) return "";
        if (this.credit.reschedule_credit === null) return "";
        return `(${this.credit.reschedule_credit})`;
      }
    },
    amount() {
      if (this.credit.amount === 0) return 0;
      const settings = this.currentCenter.currency_settings;
      const amount = this.displayPrice({ settings, price: this.credit.amount });
      if (this.credit.type === "CreditTransaction::Subtraction") {
        return `(${amount})`;
      }
      return amount;
    },
    expirationDate() {
      if (this.credit.type === "CreditTransaction::Subtraction") return "";
      const date = this.credit.expiration_date;
      const empty = "No expiration date";
      return this.displayDate({ date, format: "DD MMM YYYY", empty });
    }
  },
  watch: {},
  async mounted() {},
  methods: {
    displayDate,
    displayPrice
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/creditTransaction/Item.vue